import { __rest, __assign } from 'tslib';
import React, { forwardRef, useRef, useState, useMemo, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import TextareaAutosize from 'react-textarea-autosize';
import cn from 'classnames';
import { FormControl } from '../../form-control/esm';
import { getDataTestId } from '../../shared/esm';
import { useMedia, useFocus } from '@alfalab/hooks';
import { s as styles, P as PseudoTextArea } from './PseudoTextArea-bdf3d515.js';

var defaultColors = {"input":"textarea__input_2woln","hasInnerLabel":"textarea__hasInnerLabel_2woln","disableUserInput":"textarea__disableUserInput_2woln","error":"textarea__error_2woln","textarea":"textarea__textarea_2woln","hint":"textarea__hint_2woln"};
require('./default.css');

var invertedColors = {"input":"textarea__input_xmlxq","hasInnerLabel":"textarea__hasInnerLabel_xmlxq","disableUserInput":"textarea__disableUserInput_xmlxq","error":"textarea__error_xmlxq","textarea":"textarea__textarea_xmlxq","hint":"textarea__hint_xmlxq"};
require('./inverted.css');

var colorStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
var getDefaultCounterText = function (textLength, maxLength) {
    if (maxLength === void 0) { maxLength = 0; }
    return "".concat(textLength, "/").concat(maxLength, " \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432");
};
var Textarea = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e;
    var _f = _a.autoComplete, autoComplete = _f === void 0 ? 'on' : _f, _g = _a.autosize, autosize = _g === void 0 ? true : _g, _h = _a.size, size = _h === void 0 ? 's' : _h, _j = _a.colors, colors = _j === void 0 ? 'default' : _j, _k = _a.block, block = _k === void 0 ? false : _k, bottomAddons = _a.bottomAddons, fieldClassName = _a.fieldClassName, className = _a.className, dataTestId = _a.dataTestId, disabled = _a.disabled, error = _a.error, hint = _a.hint, textareaClassName = _a.textareaClassName, label = _a.label, _l = _a.labelView, labelView = _l === void 0 ? 'inner' : _l, leftAddons = _a.leftAddons, onFocus = _a.onFocus, onBlur = _a.onBlur, onChange = _a.onChange, onHeightChange = _a.onHeightChange, rightAddons = _a.rightAddons, maxRows = _a.maxRows, minRows = _a.minRows, maxHeight = _a.maxHeight, _m = _a.resize, resize = _m === void 0 ? 'none' : _m, value = _a.value, defaultValue = _a.defaultValue, _o = _a.rows, rows = _o === void 0 ? autosize ? 1 : 3 : _o, _p = _a.showCounter, showCounter = _p === void 0 ? false : _p, _q = _a.getCounterText, getCounterText = _q === void 0 ? getDefaultCounterText : _q, maxLength = _a.maxLength, _r = _a.allowOverflow, allowOverflow = _r === void 0 ? true : _r, nativeScrollbarProp = _a.nativeScrollbar, breakpoint = _a.breakpoint, restProps = __rest(_a, ["autoComplete", "autosize", "size", "colors", "block", "bottomAddons", "fieldClassName", "className", "dataTestId", "disabled", "error", "hint", "textareaClassName", "label", "labelView", "leftAddons", "onFocus", "onBlur", "onChange", "onHeightChange", "rightAddons", "maxRows", "minRows", "maxHeight", "resize", "value", "defaultValue", "rows", "showCounter", "getCounterText", "maxLength", "allowOverflow", "nativeScrollbar", "breakpoint"]);
    var uncontrolled = value === undefined;
    var nativeScrollbar = useMedia([[true, '(max-width: 1023px)']], false)[0];
    nativeScrollbar = Boolean(nativeScrollbarProp !== null && nativeScrollbarProp !== void 0 ? nativeScrollbarProp : nativeScrollbar);
    var textareaRef = useRef(null);
    var pseudoTextareaRef = useRef(null);
    var _s = useState(false), focused = _s[0], setFocused = _s[1];
    var _t = useState(defaultValue || ''), stateValue = _t[0], setStateValue = _t[1];
    var _u = useState(0), scrollPosition = _u[0], setScrollPosition = _u[1];
    var focusVisible = useFocus(
    /*
     * При первом рендере textareaRef.current === null, то нужно пересоздать реф для корректной работы хука
     * TODO: исправить хук useFocus, чтобы он поддерживал изменение ноды
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useMemo(function () { return ({ current: textareaRef.current }); }, [textareaRef.current]), 'keyboard')[0];
    var filled = Boolean(uncontrolled ? stateValue : value);
    var hasInnerLabel = label && labelView === 'inner';
    var hasOverflow = Boolean(maxLength && allowOverflow && ((value === null || value === void 0 ? void 0 : value.slice(maxLength)) || stateValue.slice(maxLength)));
    useEffect(function () {
        var pseudoNode = pseudoTextareaRef.current;
        if (pseudoNode) {
            pseudoNode.scrollTop = scrollPosition;
        }
    }, [scrollPosition, stateValue]);
    // Хак, так как react-textarea-autosize перестал поддерживать maxHeight
    useEffect(function () {
        if (autosize && maxHeight && textareaRef.current && textareaRef.current.style) {
            textareaRef.current.style.maxHeight = "".concat(maxHeight, "px");
        }
    }, [autosize, maxHeight]);
    var handleTextareaFocus = function (event) {
        setFocused(true);
        if (onFocus) {
            onFocus(event);
        }
    };
    var handleTextareaBlur = function (event) {
        setFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    };
    var handleTextareaChange = function (event) {
        var newValue = event.target.value;
        if (onChange) {
            onChange(event, { value: newValue });
        }
        if (uncontrolled) {
            setStateValue(newValue);
        }
    };
    var handleTeaxtareaScroll = function (event) {
        if (maxLength) {
            var val = event.target.scrollTop;
            setScrollPosition(val);
        }
    };
    var getValueLength = function () {
        if (uncontrolled) {
            return stateValue.length;
        }
        return value.length;
    };
    var textareaClassNameCalc = cn(styles.textarea, colorStyles[colors].textarea, styles[size], (_b = {},
        _b[styles.overflowHidden] = autosize && !maxRows,
        _b[styles.customScrollbar] = !nativeScrollbar,
        _b[styles.hasInnerLabel] = hasInnerLabel,
        _b[colorStyles[colors].hasInnerLabel] = hasInnerLabel,
        _b[styles.filled] = filled,
        _b), textareaClassName);
    var textareaProps = __assign(__assign({}, restProps), { className: textareaClassNameCalc, autoComplete: autoComplete, disabled: disabled, maxLength: allowOverflow ? undefined : maxLength, onBlur: handleTextareaBlur, onFocus: handleTextareaFocus, onChange: handleTextareaChange, value: uncontrolled ? stateValue : value, rows: rows, ref: mergeRefs([ref, textareaRef]), 'data-test-id': dataTestId, onScroll: handleTeaxtareaScroll });
    var getBottomAddons = function () {
        var _a;
        var counterIsVisible = Boolean(maxLength && showCounter);
        return (React.createElement(React.Fragment, null,
            counterIsVisible && (React.createElement("span", { className: cn(styles.sub, (_a = {},
                    _a[colorStyles[colors].error] = hasOverflow,
                    _a[colorStyles[colors].hint] = !hasOverflow,
                    _a)) }, getCounterText(getValueLength(), maxLength))),
            bottomAddons));
    };
    return (React.createElement(FormControl, { className: cn(className), fieldClassName: cn(fieldClassName, (_c = {},
            _c[styles.focusVisible] = focusVisible,
            _c)), inputWrapperClassName: cn(styles.wrapper, styles[size], (_d = {},
            _d[styles.hasInnerLabel] = hasInnerLabel,
            _d[styles.resizeVertical] = resize === 'vertical',
            _d)), size: size, colors: colors, block: block, disabled: disabled, filled: filled || focused, focused: focused, error: error, label: label, labelView: labelView, hint: hint, leftAddons: leftAddons, rightAddons: rightAddons, bottomAddons: getBottomAddons(), breakpoint: breakpoint, dataTestId: getDataTestId(dataTestId, 'form-control') },
        React.createElement(React.Fragment, null,
            hasOverflow && (React.createElement(PseudoTextArea, { value: value !== null && value !== void 0 ? value : stateValue, size: size, maxLength: maxLength, pseudoTextareaClassName: cn(textareaClassNameCalc, (_e = {},
                    _e[styles.nativeScrollbar] = nativeScrollbar,
                    _e)), ref: pseudoTextareaRef })),
            autosize ? (React.createElement(TextareaAutosize, __assign({}, textareaProps, { maxRows: maxRows, minRows: minRows, onHeightChange: onHeightChange }))) : (React.createElement("textarea", __assign({}, textareaProps, { style: { maxHeight: maxHeight } }))))));
});

export { Textarea, getDefaultCounterText };
